import LogManager from "./logManager";
import { callRouterLambda } from "./routerLambdaUtils";
import {
  ALLOWLIST_FLAGS,
  PERCENTAGE_FLAGS,
  BRAVEHEARTS_FLAGS,
  ORG_FLAGS,
  COUNTRY_FLAGS,
  GROUP_FLAGS,
} from "./featureFlagKeys";

// The set of flags evaluated for an in-meeting user, grouped by targeting type. This is the
// request spec handed to fetchFeatureFlags; resolveFeatureFlags below collapses the raw results
// into the app-facing booleans.
const FEATURE_FLAG_REQUEST = {
  bravehearts: [BRAVEHEARTS_FLAGS.IS_ELIGIBLE],
  allowlist: [ALLOWLIST_FLAGS.VERBOSE_SUMMARIES, ALLOWLIST_FLAGS.WHOS_WHO],
  percentage: [
    PERCENTAGE_FLAGS.VERBOSE_SUMMARIES,
    PERCENTAGE_FLAGS.AGENTIC_ASSISTANT,
    PERCENTAGE_FLAGS.WHOS_WHO,
    PERCENTAGE_FLAGS.AGENDA,
    PERCENTAGE_FLAGS.ACTION_ITEMS,
    PERCENTAGE_FLAGS.INSIGHT_PUSH,
  ],
  org: [ORG_FLAGS.BRAVEHEARTS, ORG_FLAGS.VERBOSE_SUMMARIES, ORG_FLAGS.WHOS_WHO, ORG_FLAGS.AGENDA, ORG_FLAGS.ACTION_ITEMS, ORG_FLAGS.INSIGHT_PUSH],
  country: [COUNTRY_FLAGS.AMAZON_MEETINGS_ASSISTANT_BLOCKED],
  group: [GROUP_FLAGS.AGENTIC_ASSISTANT, GROUP_FLAGS.WHOS_WHO],
};

// Flags for local dev — everything on, never legacy. Keyed off the exact localhost origin.
const LOCALHOST_FEATURE_FLAGS = {
  isLegacyUser: false,
  isVerboseSummariesEnabled: true,
  isAgenticAssistantEnabled: true,
  isWhosWhoEnabled: true,
  isAgendaEnabled: true,
  isActionItemsEnabled: true,
  isInsightPushEnabled: true,
  isAttendanceReportEnabled: true,
};

// On a fetch failure we fall legacy (the safe, non-agentic surface) with every enrichment off.
const FALLBACK_FEATURE_FLAGS = {
  isLegacyUser: true,
  isVerboseSummariesEnabled: false,
  isWhosWhoEnabled: false,
  isAgendaEnabled: false,
  isActionItemsEnabled: false,
  isInsightPushEnabled: false,
};

/**
 * Collapse the raw flag map from /feature-flags into the app-facing boolean shape. A capability is
 * on if ANY of its targeting sources (allowlist / percentage / org / group / bravehearts) resolved
 * true; isLegacyUser is driven by the country block. Pure — no I/O.
 */
export const resolveFeatureFlags = (flags) => ({
  isLegacyUser: flags?.[COUNTRY_FLAGS.AMAZON_MEETINGS_ASSISTANT_BLOCKED] === true,
  isAttendanceReportEnabled: true,
  isVerboseSummariesEnabled: flags?.[ALLOWLIST_FLAGS.VERBOSE_SUMMARIES] === true
    || flags?.[PERCENTAGE_FLAGS.VERBOSE_SUMMARIES] === true
    || flags?.[BRAVEHEARTS_FLAGS.IS_ELIGIBLE] === true
    || flags?.[ORG_FLAGS.VERBOSE_SUMMARIES] === true,
  isAgenticAssistantEnabled: flags?.[BRAVEHEARTS_FLAGS.IS_ELIGIBLE] === true
    || flags?.[PERCENTAGE_FLAGS.AGENTIC_ASSISTANT] === true
    || flags?.[ORG_FLAGS.BRAVEHEARTS] === true
    || flags?.[GROUP_FLAGS.AGENTIC_ASSISTANT] === true,
  isWhosWhoEnabled: flags?.[ALLOWLIST_FLAGS.WHOS_WHO] === true
    || flags?.[PERCENTAGE_FLAGS.WHOS_WHO] === true
    || flags?.[BRAVEHEARTS_FLAGS.IS_ELIGIBLE] === true
    || flags?.[ORG_FLAGS.WHOS_WHO] === true
    || flags?.[GROUP_FLAGS.WHOS_WHO] === true,
  isAgendaEnabled: flags?.[PERCENTAGE_FLAGS.AGENDA] === true
    || flags?.[ORG_FLAGS.AGENDA] === true,
  isActionItemsEnabled: flags?.[PERCENTAGE_FLAGS.ACTION_ITEMS] === true
    || flags?.[ORG_FLAGS.ACTION_ITEMS] === true,
  isInsightPushEnabled: flags?.[PERCENTAGE_FLAGS.INSIGHT_PUSH] === true
    || flags?.[ORG_FLAGS.INSIGHT_PUSH] === true,
});

/**
 * Resolve the feature-flag set for the current user/route. Handles the local-dev and /login
 * short-circuits, fetches + resolves otherwise, and falls back to the legacy-safe set on error.
 * Returns the app-facing flags object. Pure orchestration — no React state.
 */
export const loadFeatureFlags = async () => {
  if (window.location.href === "http://localhost:3000/") {
    return LOCALHOST_FEATURE_FLAGS;
  }
  if (window.location.pathname === "/login") {
    return { isLegacyUser: false };
  }
  try {
    const flags = await fetchFeatureFlags(FEATURE_FLAG_REQUEST);
    // fetchFeatureFlags returns null on failure (doesn't throw), so guard here.
    if (!flags) {
      return FALLBACK_FEATURE_FLAGS;
    }
    const resolvedFlags = resolveFeatureFlags(flags);
    LogManager.info("Feature flags resolved:", JSON.stringify(resolvedFlags));
    return resolvedFlags;
  } catch (error) {
    LogManager.error("Error fetching feature flags:", error);
    return FALLBACK_FEATURE_FLAGS;
  }
};

/**
 * Fetches feature flag evaluations from the backend.
 * Automatically appends required context based on flag type.
 *
 * @param {Object} params
 * @param {string[]} [params.allowlist] - Allowlist flag keys (no extra context needed)
 * @param {string[]} [params.percentage] - Percentage flag keys (no extra context needed)
 * @param {string[]} [params.org] - Org flag keys (requires supervisorChain context)
 * @param {string[]} [params.bravehearts] - Bravehearts flag keys (checks bravehearts DDB membership)
 * @param {string[]} [params.group] - Group flag keys — checks BRASS BatchIsMemberOf (groupType/groupIds from AppConfig)
 * @param {string[]} [params.country] - Country flag keys — passes user's country to AppConfig for variant rule evaluation
 * @returns {Promise<Object|null>} Map of flagKey -> boolean, or null on error
 */
export const fetchFeatureFlags = async ({ allowlist = [], percentage = [], org = [], bravehearts = [], group = [], country = [] }) => {
  try {
    const flags = {};
    for (const key of allowlist) flags[key] = [];
    for (const key of percentage) flags[key] = [];
    for (const key of org) flags[key] = ["supervisorChain"];
    for (const key of bravehearts) flags[key] = ["bravehearts"];
    for (const key of group) flags[key] = ["group"];
    for (const key of country) flags[key] = ["country"];

    if (Object.keys(flags).length === 0) {
      LogManager.warn("fetchFeatureFlags called with no flags");
      return null;
    }

    const payload = await callRouterLambda("/feature-flags", { flags });
    LogManager.info("Parsed /feature-flags payload:", payload);

    return payload;
  } catch (error) {
    LogManager.error("Error calling /feature-flags:", error);
    return null;
  }
};
